import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { BillingCycleModalComponent } from 'src/app/components/vendor-products/modals/billing-cycle-modal/billing-cycle-modal.component';
import { Partner, PartnersService } from 'src/app/services/partners/partners.service';
import {
  BILLING_CYCLE_LABELS,
  IBillingCycle,
  IBrandBillingCycle,
  ProductsSubprojectService,
} from 'src/app/services/products-subproject/products-subproject.service';

@Component({
  selector: 'app-brands-table',
  templateUrl: './brands-table.component.html',
  styleUrls: ['./brands-table.component.scss'],
})
export class BrandsTableComponent implements OnInit, AfterViewInit {
  @Input() subProjectProductId!: string;
  @Input() billingCycles: IBillingCycle[] = [];
  @ViewChild(MatSort) sort!: MatSort;
  public columnNames = ['logoUrl', 'name', 'redirectUrl', 'billingCycle', 'action'];
  public tableDataSource = new MatTableDataSource<Partner>();
  public brands: Partner[] = [];
  public brandsBillingCycle: IBrandBillingCycle[] = [];

  constructor(
    private readonly partnersService: PartnersService,
    private readonly productsSubprojectService: ProductsSubprojectService,
    private readonly snackBar: MatSnackBar,
    private readonly dialog: MatDialog,
  ) {}

  async ngOnInit() {
    await this.loadPartners();
    if (this.sort) this.tableDataSource.sort = this.sort;
  }

  ngAfterViewInit(): void {
    this.tableDataSource.sort = this.sort;
  }

  private async loadPartners() {
    const partnersResponse = await this.partnersService.getAllPartners();
    this.tableDataSource.data = partnersResponse;
    this.brands = partnersResponse;
    if (this.subProjectProductId) await this.getBrandsBillingCycle();
  }

  private async getBrandsBillingCycle() {
    const brandsBillingCycleResponse =
      await this.productsSubprojectService.getBrandsBillingCycleByProductId(
        this.subProjectProductId,
      );
    if (brandsBillingCycleResponse.success)
      this.brandsBillingCycle = brandsBillingCycleResponse.data;
    else
      this.snackBar.open('Error fetching brands billing cycle information', 'Close', {
        duration: 3000,
      });
  }

  private getBillingCycle(
    partnerId: string,
  ): (IBillingCycle & { billingDay: number | null }) | null {
    const brandBillingCycle = this.brandsBillingCycle.find(
      (brandBC) => brandBC.brandId === partnerId,
    );
    if (brandBillingCycle) {
      const billingCycle = this.billingCycles.find(
        (bc) => bc.id === brandBillingCycle.billingCycleId,
      );
      if (billingCycle) {
        return { ...billingCycle, billingDay: brandBillingCycle.billingDay };
      }
    }
    return null;
  }

  public getBillingCycleLabel(partnerId: string): string {
    const billingCycle = this.getBillingCycle(partnerId);
    if (billingCycle)
      return BILLING_CYCLE_LABELS[billingCycle.type as keyof typeof BILLING_CYCLE_LABELS];
    return 'Default Billing';
  }

  public openBillingCycleModal(partner: Partner) {
    const billingCycle = this.getBillingCycle(partner.partnerId as string);
    const dialogRef = this.dialog.open(BillingCycleModalComponent, {
      data: {
        billingCycles: this.billingCycles,
        productId: this.subProjectProductId,
        resourceId: partner.partnerId,
        saveFunction: this.saveFunction.bind(this),
        billingCycleInfo: {
          billingCycleId: billingCycle ? billingCycle.id : null,
          billingDay: billingCycle ? billingCycle.billingDay : undefined,
        },
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadPartners();
      }
    });
  }

  private saveFunction(data: {
    resourceId: string;
    productId: string;
    billingCycleId: number | null;
    billingDay?: number;
  }) {
    if (data.billingCycleId === null) {
      return this.productsSubprojectService.deleteBrandBillingCycle(
        data.productId,
        data.resourceId,
      );
    } else {
      return this.productsSubprojectService.createOrUpdateBrandBillingCycle(
        data.productId,
        data.resourceId,
        data.billingCycleId,
        data.billingDay,
      );
    }
  }
}
